import { useState } from 'react';
import { PlanDetailsType } from 'src/types/types';
import { SubscriptionUsageSection } from './SubscriptionUsageSection';
import { TicketPacksSection } from './TicketPacksSection';

type CreditBalancePanelProps = {
  planDetails: PlanDetailsType | null;
  subscription: any;
};

export const CreditBalancePanel = ({ planDetails, subscription }: CreditBalancePanelProps) => {
  const [subscriptionExpanded, setSubscriptionExpanded] = useState(false);
  const [packsExpanded, setPacksExpanded] = useState(false);

  // Calculate totals using the subscription object
  const subscriptionRemaining =
    planDetails?.subscription?.tickets_remaining ||
    Math.max(0, (planDetails?.monthly_quota || 0) - (planDetails?.subscription?.tickets_used || 0));
  const packTicketsRemaining = planDetails?.pack_ticket_balance || 0;
  const totalAvailable = subscriptionRemaining + packTicketsRemaining;

  // Determine subscription renewal date
  const renewalDate = subscription?.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    : 'N/A';

  return (
    <div>
      <h3 className="text-xl font-bold mb-3">Ticket Allowance</h3>
      <p className="text-sm text-600 mb-4">
        We'll use your renewable tickets before non-renewable ones
      </p>

      {/* Subscription Section - Collapsible */}
      <div
        className="mb-3 p-3 border-round cursor-pointer"
        style={{
          backgroundColor: subscriptionExpanded ? '#f8f9fa' : 'white',
          border: '1px solid #e0e0e0',
        }}
        onClick={() => setSubscriptionExpanded(!subscriptionExpanded)}
      >
        <div className="flex align-items-center justify-content-between">
          <div className="flex align-items-center gap-2">
            <i className={`pi pi-chevron-${subscriptionExpanded ? 'down' : 'right'}`} />
            <span className="font-semibold">Subscription</span>
            {!subscriptionExpanded && (
              <span className="text-sm text-600">(renews {renewalDate})</span>
            )}
          </div>
          <span className="font-bold">{subscriptionRemaining} left</span>
        </div>

        {subscriptionExpanded && (
          <SubscriptionUsageSection
            subscription={subscription}
            planDetails={planDetails}
            renewalDate={renewalDate}
          />
        )}
      </div>

      {/* Ticket Packs Section - Collapsible */}
      {packTicketsRemaining > 0 && (
        <div
          className="mb-3 p-3 border-round cursor-pointer"
          style={{
            backgroundColor: packsExpanded ? '#f8f9fa' : 'white',
            border: '1px solid #e0e0e0',
          }}
          onClick={() => setPacksExpanded(!packsExpanded)}
        >
          <div className="flex align-items-center justify-content-between">
            <div className="flex align-items-center gap-2">
              <i className={`pi pi-chevron-${packsExpanded ? 'down' : 'right'}`} />
              <span className="font-semibold">Ticket Packs</span>
            </div>
            <span className="font-bold">{packTicketsRemaining.toLocaleString()} left</span>
          </div>

          {packsExpanded && <TicketPacksSection planDetails={planDetails} />}
        </div>
      )}

      {/* Total Available */}
      <div className="p-3 border-round" style={{ backgroundColor: '#f0f9ff' }}>
        <div className="flex align-items-center justify-content-between">
          <span className="font-bold text-lg">Total Available</span>
          <span className="font-bold text-2xl" style={{ color: '#0284c7' }}>
            {totalAvailable.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
};
